import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const MobileAppContent = ({ artistData, currentSection }) => {
  const renderHomeScreen = () => (
    <div className="relative h-full">
      {/* Splash/Background Image */}
      <div className="absolute inset-0">
        <Image 
          src={artistData?.promotion?.splashImage || "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400"}
          alt="Artist Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
      </div>
      
      {/* Content Overlay */}
      <div className="relative z-10 flex flex-col h-full">
        {/* Header with Artist Logo - UPDATED */}
        <div className="flex justify-center pt-8 pb-4">
          <div className="w-20 h-20 bg-white rounded-full p-2 shadow-lg">
            <Image 
              src={
                artistData?.profile?.artistLogoUrl || 
                artistData?.promotion?.logo || 
                "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=100"
              }
              alt="Artist Logo"
              className="w-full h-full object-contain rounded-full"
            />
          </div>
        </div>
        
        {/* Artist Name */}
        <div className="text-center px-6">
          <h1 className="text-2xl font-bold text-white mb-2">{artistData?.profile?.name || "Artist Name"}</h1>
          <p className="text-white opacity-90 text-sm">{artistData?.profile?.artist_type || "Artist"}</p>
        </div>
        
        {/* Navigation Menu */}
        <div className="flex-1 flex items-end pb-8">
          <div className="w-full px-6 space-y-3">
            {['About', 'Calendar', 'Work', 'Shop']?.map((item) => (
              <div key={item} className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 flex items-center justify-between">
                <span className="text-white font-medium">{item}</span>
                <Icon name="ChevronRight" size={16} className="text-white" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderAboutScreen = () => (
    <div className="p-6 space-y-6">
      {/* Header Image */}
      <div className="w-full h-48 rounded-lg overflow-hidden">
        <Image 
          src={artistData?.about?.headerImage || "https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=400"}
          alt="About Header"
          className="w-full h-full object-cover"
        />
      </div>
      
      {/* Artist Logo Section - NEW */}
      {artistData?.profile?.artistLogoUrl && (
        <div className="flex justify-center">
          <div className="w-24 h-24 bg-white rounded-full p-2 shadow-md border-2 border-gray-100">
            <Image 
              src={artistData?.profile?.artistLogoUrl}
              alt="Artist Logo"
              className="w-full h-full object-contain rounded-full"
            />
          </div>
        </div>
      )}
      
      {/* About Text */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-3">About {artistData?.profile?.name}</h2>
        <p className="text-gray-700 leading-relaxed">
          {artistData?.about?.aboutText || `${artistData?.profile?.name} is a talented ${artistData?.profile?.artist_type?.toLowerCase()} with years of experience in the industry. Known for innovative work and creative excellence.`}
        </p>
      </div>
      
      {/* Contact Info */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="font-semibold text-gray-900 mb-3">Contact Information</h3>
        <div className="space-y-2">
          <div className="flex items-center space-x-3">
            <Icon name="Mail" size={16} className="text-gray-500" />
            <span className="text-sm text-gray-700">{artistData?.profile?.email || "contact@artist.com"}</span>
          </div>
          <div className="flex items-center space-x-3">
            <Icon name="Phone" size={16} className="text-gray-500" />
            <span className="text-sm text-gray-700">{artistData?.profile?.phone || "+1 (555) 123-4567"}</span>
          </div>
          <div className="flex items-center space-x-3">
            <Icon name="Globe" size={16} className="text-gray-500" />
            <span className="text-sm text-gray-700">{artistData?.profile?.website || "www.artist.com"}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCalendarScreen = () => (
    <div className="p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-6">Upcoming Events</h2>
      <div className="space-y-4">
        {(artistData?.calendar || [
          {
            id: 1,
            title: "Live Performance",
            date: "2025-01-15",
            time: "8:00 PM",
            venue: "Madison Square Garden",
            image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=300"
          },
          {
            id: 2,
            title: "Art Exhibition Opening",
            date: "2025-01-22",
            time: "6:00 PM",
            venue: "Modern Art Gallery",
            image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=300"
          }
        ])?.map((event) => (
          <div key={event?.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="flex">
              <div className="w-20 h-20 flex-shrink-0">
                <Image 
                  src={event?.image}
                  alt={event?.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 p-4">
                <h3 className="font-semibold text-gray-900 mb-1">{event?.title}</h3>
                <p className="text-sm text-gray-600 mb-1">{event?.venue}</p>
                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  <span>{new Date(event.date)?.toLocaleDateString()}</span>
                  <span>{event?.time}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderWorkScreen = () => {
    const artistType = artistData?.profile?.artistType?.toLowerCase() || 'music';
    
    if (artistType === 'music') {
      return (
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Music</h2>
          <div className="space-y-4">
            {['Spotify', 'Apple Music', 'YouTube Music', 'Tidal']?.map((platform) => (
              <div key={platform} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Icon name="Music" size={20} className="text-gray-600" />
                  </div>
                  <span className="font-medium text-gray-900">Listen on {platform}</span>
                </div>
                <Icon name="ExternalLink" size={16} className="text-gray-400" />
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    return (
      <div className="p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Portfolio</h2>
        <div className="grid grid-cols-2 gap-4">
          {(artistData?.work || [
            {
              id: 1,
              title: "Artwork 1",
              image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=300",
              price: "$500"
            },
            {
              id: 2,
              title: "Artwork 2", 
              image: "https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=300",
              price: "$750"
            }
          ])?.map((work) => (
            <div key={work?.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="aspect-square">
                <Image 
                  src={work?.image}
                  alt={work?.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-3">
                <h3 className="font-medium text-gray-900 text-sm mb-1">{work?.title}</h3>
                <p className="text-xs text-gray-600">{work?.price}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderShopScreen = () => (
    <div className="p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-6">Shop</h2>
      <div className="grid grid-cols-2 gap-4">
        {[
          { name: 'T-Shirt', price: '$25', image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=300' },
          { name: 'Poster', price: '$15', image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=300' },
          { name: 'Vinyl Album', price: '$35', image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=300' },
          { name: 'Tote Bag', price: '$20', image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=300' }
        ]?.map((item, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="aspect-square">
              <Image 
                src={item?.image}
                alt={item?.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-3">
              <h3 className="font-medium text-gray-900 text-sm mb-1">{item?.name}</h3>
              <p className="text-xs text-gray-600">{item?.price}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (currentSection) {
      case 'home':
        return renderHomeScreen();
      case 'about':
        return renderAboutScreen();
      case 'calendar':
        return renderCalendarScreen();
      case 'work':
        return renderWorkScreen();
      case 'shop':
        return renderShopScreen();
      default:
        return renderHomeScreen();
    }
  };

  return renderContent();
};

export default MobileAppContent;